import React, { useRef, useEffect } from 'react';
import { DatePicker } from 'antd';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const StyledCalendarBox = styled(Box)({
  backgroundColor: '#3e3c56',
  borderRadius: '8px',
  padding: '16px',
  width: '100%',
  maxWidth: '305px',
  margin: '0 auto',
  '.ant-picker': {
    width: '100%',
    border: 'none',
    padding: 0,
    backgroundColor: 'transparent',
    '& .ant-picker-input': {
      display: 'none'
    }
  },
  '.ant-picker-dropdown': {
    position: 'static !important',
    margin: '0 !important',
    padding: '0 !important',
    width: '100%',
    boxShadow: 'none',
    backgroundColor: 'transparent',
    '.ant-picker-panel-container': {
      backgroundColor: '#3e3c56',
      boxShadow: 'none',
      border: 'none',
      minWidth: '310px',
    },
    '.ant-picker-header': {
      color: '#fff',
      border: 'none',
      backgroundColor: '#3e3c56',
      margin: '0 0 16px 0',
      padding: 0,
      button: {
        color: '#fff',
        fontSize: '18px',
        padding: '4px',
        '&:hover': {
          color: '#ff4e3a'
        }
      },
      '.ant-picker-header-view': {
        color: '#fff',
        fontSize: '16px',
        fontWeight: 500
      }
    },
    '.ant-picker-content': {
      backgroundColor: '#3e3c56',
      width: '110%',
      table: {
        width: '100%',
        tableLayout: 'fixed',
        borderSpacing: '4px',
        borderCollapse: 'separate'
      },
      'thead tr': {
        height: '32px',
        th: {
          color: '#fff',
          backgroundColor: '#2b2937',
          fontWeight: 500,
          fontSize: '14px',
          padding: '4px 0',
          width: '32px',
          textAlign: 'center',
          borderRadius: '6px'
        }
      },
      'tbody tr': {
        height: '32px',
        td: {
          padding: 0,
          width: '32px',
          textAlign: 'center'
        }
      }
    },
    '.ant-picker-cell': {
      color: '#fff',
      backgroundColor: 'transparent',
      padding: 0,
      '&:hover .ant-picker-cell-inner': {
        background: 'rgba(255, 78, 58, 0.2)'
      },
      '&-in-view': {
        color: '#fff',
        '&.ant-picker-cell-selected .ant-picker-cell-inner': {
          backgroundColor: '#ff4e3a',
          color: '#fff'
        },
        '&.ant-picker-cell-today .ant-picker-cell-inner::before': {
          borderColor: '#ff4e3a'
        },
        '&.ant-picker-cell-in-range': {
          '&::before': {
            background: 'rgba(255, 78, 58, 0.1)'
          }
        },
        '&.ant-picker-cell-range-start .ant-picker-cell-inner, &.ant-picker-cell-range-end .ant-picker-cell-inner': {
          backgroundColor: '#ff4e3a'
        }
      }
    },
    '.ant-picker-cell-inner': {
      color: '#fff',
      backgroundColor: 'transparent',
      borderRadius: '50%',
      width: '28px',
      height: '28px',
      lineHeight: '28px',
      fontSize: '14px',
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    '.ant-picker-footer': {
      display: 'none'
    }
  }
});

const Calendar = ({ 
  isOpen,
  selectedDates,
  isRangeSelection,
  handleDateClick,
  rangeStart,
  forceOpen = true
}) => {
  const containerRef = useRef(null);
  const pickerRef = useRef(null);

  // This effect handles keeping the calendar open
  useEffect(() => {
    if (!isOpen) return;

    const keepCalendarOpen = () => {
      // Find the DatePicker dropdown
      const dropdown = document.querySelector('.ant-picker-dropdown');
      if (!dropdown) return;

      // Remove any hidden class that might be added
      dropdown.classList.remove('ant-picker-dropdown-hidden');
      
      // Force the dropdown to be visible
      dropdown.style.display = 'block';
      dropdown.style.opacity = '1';
      dropdown.style.pointerEvents = 'auto';
    };

    // Set up an interval to keep checking and forcing the calendar open
    const interval = setInterval(keepCalendarOpen, 100);

    return () => {
      clearInterval(interval);
    };
  }, [isOpen]);

  const disabledDate = (current) => {
    return current && current < dayjs().startOf('day');
  };

  if (!isOpen) return null;

  const selectedDayjsDates = selectedDates.map(date => 
    dayjs(date.split('-').reverse().join('-')).format('YYYY-MM-DD')
  );

  return (
    <StyledCalendarBox ref={containerRef}>
      <div ref={pickerRef}>
        <DatePicker
          onChange={handleDateClick}
          disabledDate={disabledDate}
          format="DD-MM-YYYY"
          allowClear={false}
          open={true}
          onOpenChange={(open) => {
            // Always keep the calendar open
            return true;
          }}
          getPopupContainer={() => containerRef.current}
          style={{ width: '100%' }}
          popupStyle={{ position: 'static' }}
          dateRender={(current) => {
            const formattedCurrentDate = current.format('DD-MM-YYYY');
            const isSelected = selectedDayjsDates.includes(current.format('YYYY-MM-DD'));
            const isRangeStartDate = rangeStart && formattedCurrentDate === rangeStart;
            
            let isInRange = false;
            if (isRangeSelection && rangeStart) {
              const rangeStartDate = dayjs(rangeStart, 'DD-MM-YYYY');
              const currentDateObj = dayjs(formattedCurrentDate, 'DD-MM-YYYY');
              
              if (
                (currentDateObj.isAfter(rangeStartDate) && currentDateObj.isBefore(dayjs())) ||
                (currentDateObj.isBefore(rangeStartDate) && currentDateObj.isAfter(dayjs()))
              ) {
                isInRange = true;
              }
            }
            
            let backgroundColor = 'transparent';
            let border = 'none';
            
            if (isSelected) {
              backgroundColor = '#ff4e3a';
            }
            
            if (isRangeStartDate && isRangeSelection) {
              backgroundColor = '#ff4e3a';
              border = '2px solid #fff';
            }

            if (isInRange && isRangeSelection) {
              backgroundColor = 'rgba(255, 78, 58, 0.3)';
            }
            
            return (
              <div
                className="ant-picker-cell-inner"
                style={{
                  backgroundColor,
                  color: isSelected || isInRange ? '#fff' : undefined,
                  border,
                  boxSizing: 'border-box'
                }}
              >
                {current.date()}
              </div>
            );
          }}
        />
      </div>
    </StyledCalendarBox>
  );
};

export default Calendar;